import { useQuery } from '@tanstack/react-query'

import type { UseQueryResult } from '@tanstack/react-query'
import type { IABTestEngine, IEngine, IABTestVariation, IVersionEngine } from '~/types/arbitration'

import { getEngine, getPlacementHistory } from '~/api/arbitration'
import { ENGINE_TYPE } from '~/types/arbitration'

////////// KEYS //////////
export const arbitrationKeys = {
  all: ['arbitration'] as const,
  archiveEngine: (appKey: string, deviceType: string) =>
    [...arbitrationKeys.all, 'archive-engine', appKey, deviceType] as const,
  engine: (engineType: string, appKey: string, deviceType: string) =>
    [...arbitrationKeys.all, 'engine', engineType, appKey, deviceType] as const,
  placementHistory: (placementId: string, appKey: string, deviceType: string) =>
    [...arbitrationKeys.all, 'placement-history', placementId, appKey, deviceType] as const,
}

////////// QUERIES //////////
export const useEngineQuery = ({
  appKey,
  deviceType,
  engineType,
}: {
  appKey?: string
  deviceType?: string
  engineType?: ENGINE_TYPE
}): UseQueryResult<IEngine | IABTestEngine | IVersionEngine[]> => {
  return useQuery({
    enabled: !!appKey && !!deviceType && !!engineType,
    queryFn: () =>
      getEngine({
        appKey: appKey as string,
        deviceType: deviceType as string,
        engineType: engineType as ENGINE_TYPE,
      }),
    queryKey: arbitrationKeys.engine(engineType as string, appKey as string, deviceType as string),
    select: (data) => {
      if (engineType === ENGINE_TYPE.AB_TEST) {
        return Object.entries(data)
          .map(([placement, variation]) => ({ placement, variation }))
          .sort((a, b) => {
            const timestampA = a.variation.composition.control_variation ? a.variation.created_at_ts : 0
            const timestampB = b.variation.composition.control_variation ? b.variation.created_at_ts : 0
            return timestampA > timestampB ? -1 : 1
          })
          .reduce((prev, { placement, variation }) => ({ ...prev, [placement]: variation }), {} as IABTestEngine)
      }
      return data
    },
  })
}

export const usePlacementHistoryQuery = ({
  appKey,
  deviceType,
  placementId,
}: {
  appKey: string
  deviceType: string
  placementId: string
}): UseQueryResult<IABTestVariation[]> => {
  return useQuery({
    queryFn: () =>
      getPlacementHistory({
        appKey: appKey,
        deviceType: deviceType,
        placementId: placementId,
      }),
    queryKey: arbitrationKeys.placementHistory(placementId, appKey, deviceType),
  })
}

export const useVersionArchiveEngineQuery = ({
  appKey,
  deviceType,
  engineType,
}: {
  appKey?: string
  deviceType?: string
  engineType: ENGINE_TYPE.VERSION | ENGINE_TYPE.ARCHIVE
}): UseQueryResult<IVersionEngine[]> => {
  return useQuery({
    enabled: !!appKey && !!deviceType,
    queryFn: () =>
      getEngine({
        appKey: appKey as string,
        deviceType: deviceType as string,
        engineType,
      }),
    queryKey: arbitrationKeys.engine(engineType, appKey as string, deviceType as string),
  })
}
